//component for mill bar feeder

import { isPlatformBrowser } from '@angular/common';
import { Component, inject, PLATFORM_ID } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';

import { AuiCheckboxComponent } from '@ral/ui:aui/checkbox';
import { AuiFormFieldComponent, AuiFormLayoutComponent } from '@ral/ui:aui/form-layout';
import { AuiInputComponent } from '@ral/ui:aui/input';
import { markAllControlsAsDirty } from '@ral/utils:core/forms';
import { injectToastService } from '@ral/utils:core/toast';
import { StoryDescriptionComponent } from '@storyteller/description';
import { StoryTitleComponent } from '@storyteller/title';
import { injectNewsletterCreateMutation } from '../mutations/newsletter.mutation';
import { AuiItemComponent, AuiLoadingDirective } from '@ral/ui:aui/item';
import { AuiLoadingComponent } from '@ral/ui:aui/aui-loading';

@Component({
    selector: 'app-newsletter',
    imports: [
        StoryTitleComponent,
        StoryDescriptionComponent,
        AuiFormLayoutComponent,
        AuiFormFieldComponent,
        AuiInputComponent,
        AuiCheckboxComponent,
        AuiLoadingDirective,
        AuiItemComponent,
        ReactiveFormsModule,
        AuiLoadingComponent,
    ],
    template: `
        <div aria-live="polite" aria-atomic="true">
            @defer (on viewport) {
                <div class="flex flex-col gap-6 md:flex-row">
                    <div class="flex-1">
                        <div class="max-w-[560px]">
                            <story-title [headingLevel]="3" className="story-font-headline3 pb-2"
                                >Chcete dostávat novinky a&nbsp;inspiraci ze světa automatizace?</story-title
                            >
                            <story-description [className]="'pb-4 max-w-[450px]'">
                                Žádný spam, jen občasné novinky, zajímavé projekty a&nbsp;praktické tipy z&nbsp;praxe.
                            </story-description>
                        </div>
                    </div>
                    <div class="flex-1">
                        <form [formGroup]="form" (ngSubmit)="submit()">
                            <div class="flex flex-col gap-4 sm:flex-row">
                                <div class="flex-grow">
                                    <aui-form-layout>
                                        <label for="email" class="absolute left-[-100vw] top-0">Emailová adresa</label>
                                        <aui-form-field [name]="'email'">
                                            <aui-input
                                                [placeholder]="'Emailová adresa'"
                                                [formControl]="form.controls.email"
                                                [size]="'48'"
                                                [rounded]="false"
                                                type="email"
                                                autoCompleteProp="email"
                                                [aria-label]="'Emailová adresa'"
                                            />
                                        </aui-form-field>
                                        <aui-form-field>
                                            <aui-checkbox
                                                [formControl]="form.controls.confirmation"
                                                [checkboxLabel]="'Souhlasím se '"
                                            >
                                                <a
                                                    href="/assets/privacy-policy.pdf"
                                                    target="_blank"
                                                    class="link"
                                                    rel="noopener noreferrer"
                                                    aria-label="Zpracováním osobních údajů (PDF, méně než 1 MB)"
                                                >
                                                    zpracováním osobních údajů</a
                                                >
                                            </aui-checkbox>
                                        </aui-form-field>
                                    </aui-form-layout>
                                </div>
                                <div class="flex-none">
                                    <button
                                        auiItem
                                        type="submit"
                                        [size]="'48'"
                                        [color]="'primary'"
                                        type="submit"
                                        [auiLoading]="mutation.isPending()"
                                        [disabled]="mutation.isPending()"
                                    >
                                        <span auiItemLabel>Odebírat</span>
                                    </button>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            } @placeholder {
                <div class="p-2 text-center">
                    <aui-loading />
                    <p class="story-gray-text mt-4">Načítání...</p>
                </div>
            }
        </div>
    `,
})
export class NewsletterComponent {
    platform = inject(PLATFORM_ID);
    mutation = injectNewsletterCreateMutation();
    toastService = injectToastService();
    form = new FormGroup({
        email: new FormControl('', [Validators.required, Validators.email]),
        confirmation: new FormControl(false, [Validators.requiredTrue]),
    });

    submit() {
        this.form.markAllAsTouched();
        markAllControlsAsDirty(this.form);
        this.form.updateValueAndValidity({ emitEvent: false });

        if (this.form.invalid) {
            return;
        }

        if (isPlatformBrowser(this.platform)) {
            (window as any).dataLayer = (window as any).dataLayer || [];
            (window as any).dataLayer.push({
                event: 'newsletter_subscribe',
                email: this.form.value.email!,
                consentId:
                    document.cookie
                        .split('; ')
                        .find((row) => row.startsWith('cookieyes-consent='))
                        ?.split('=')[1] || '',
                gclId:
                    document.cookie
                        .split('; ')
                        .find((row) => row.startsWith('gclid='))
                        ?.split('=')[1] || '',
            } as any);
        }

        this.mutation.mutateAsync(
            {
                emailAddress: this.form.value.email!,
            },
            {
                onSuccess: () => {
                    this.form.reset();
                },
            }
        );
    }
}
